import {
  ACCOUNT_SUBSCRIPTION_TYPE,
  BUSINESS_PLATFORM,
  ONBOARDING_PLATFORM,
} from '@adtrackify/at-tracking-event-types';

import { useShopifyIntegrationDestination } from '@/features/destinations/hooks/use-destination-by-type';
import { useAccountState } from '@/contexts/account/account-context';
import type {
  ShopifyDataInfo,
} from '../types/use-has-shopify-data-types';
import { SHOPIFY_DATA_SOURCE } from '../types/use-has-shopify-data-enums';

export function useHasShopifyData(): ShopifyDataInfo {
  const { currentAccount, hasLoadedAccounts } = useAccountState();
  const shopifyIntegration = useShopifyIntegrationDestination();

  if (!currentAccount) {
    return { hasShopifyData: false, isLoading: !hasLoadedAccounts, source: SHOPIFY_DATA_SOURCE.NONE };
  }

  const subscriptionType = currentAccount.accountSubscriptionType;
  const isPlatformMigrated = currentAccount.isPlatformMigrated ?? false;
  const selectedShopifyAtOnboarding =
    currentAccount.onboardingPlatform === ONBOARDING_PLATFORM.SHOPIFY ||
    currentAccount.personalization?.businessPlatform === BUSINESS_PLATFORM.SHOPIFY;

  if (!hasLoadedAccounts) {
    if (subscriptionType === ACCOUNT_SUBSCRIPTION_TYPE.SHOPIFY || isPlatformMigrated) {
      return { hasShopifyData: true, isLoading: true, source: SHOPIFY_DATA_SOURCE.SHOPIFY_APP };
    }

    if (selectedShopifyAtOnboarding) {
      return { hasShopifyData: true, isLoading: true, source: SHOPIFY_DATA_SOURCE.ONBOARDING_INTENT };
    }

    return { hasShopifyData: false, isLoading: true, source: SHOPIFY_DATA_SOURCE.NONE };
  }

  const isShopifyBilling = subscriptionType === ACCOUNT_SUBSCRIPTION_TYPE.SHOPIFY;
  if (isShopifyBilling || isPlatformMigrated) {
    return { hasShopifyData: true, isLoading: false, source: SHOPIFY_DATA_SOURCE.SHOPIFY_APP };
  }

  if (shopifyIntegration) {
    return { hasShopifyData: true, isLoading: false, source: SHOPIFY_DATA_SOURCE.DATA_SOURCE };
  }

  if (selectedShopifyAtOnboarding) {
    return { hasShopifyData: true, isLoading: false, source: SHOPIFY_DATA_SOURCE.ONBOARDING_INTENT };
  }

  return { hasShopifyData: false, isLoading: false, source: SHOPIFY_DATA_SOURCE.NONE };
}
